// Scripts page: constants and pure helpers shared by the list (ScriptList) and the editor (ScriptEditor).
// HD-7 split: moved from pages/Scripts.jsx (lines 20-793) with `export` added; the old list-only constants (chip classes,
// list headers and column widths) went with the list migration.

// ── Constants ────────────────────────────────────────────────────

// 'Filming' is NOT a qual_status — it is the in-production shelf (the script's
// video card has reached Filming or later, so the script has left the writing
// queue and the default list hides it). It sits in the chip row in pipeline
// order, between Approved and Posted, and is the only way back to those
// scripts from this page. The server owns the shelf (?qualStatus=Filming).
export const QUAL_STATUSES = ['I/P', 'Needs Review', 'Needs Revision', 'Approved', 'Filming', 'Posted', 'Backburner'];

export const TRANSITIONS = {
  // Roles mirror the server exactly (TRANSITIONS in routes/scripts.js): Needs
  // Review is a WRITER-side move — Writer / Power User / Admin. A Qual Reviewer
  // used to be offered the button here and got a 403 from the server every time.
  // From 'Approved' this is the pull-back (Owner 2026-09-08): allowed only while
  // the script is still just a script, which the server re-checks and 409s.
  'Needs Review':   { from: ['I/P', 'Needs Revision', 'Approved'],                roles: ['Writer', 'Admin', 'Power User'] },
  'Approved':       { from: ['Needs Review'],                                      roles: ['Qual Reviewer', 'Admin', 'Power User'], noSelf: true },
  'Needs Revision': { from: ['Needs Review', 'Approved'],                          roles: ['Qual Reviewer', 'Admin', 'Power User'], noSelf: true },
  'I/P':            { from: ['Needs Revision', 'Backburner'],                      roles: ['Writer', 'Qual Reviewer', 'Admin', 'Power User'] },
  'Backburner':     { from: ['I/P', 'Needs Review', 'Needs Revision', 'Approved'], roles: ['Writer', 'Qual Reviewer', 'Admin', 'Power User'] },
};

export const FORMAT_COLS = {
  // Longform (D-089): Skit-shaped rows — the series' dialogue/breakdown, so the
  // character / casting / Scene Scrub machinery works by construction.
  Longform: [
    { key: 'sceneNumber', label: 'Scene #',           cls: 'notes',     rows: 1, width: '8%'  },
    { key: 'character',   label: 'Character',         cls: 'character', rows: 1, width: '16%' },
    { key: 'line',        label: 'Line',              cls: 'line',      rows: 2, width: '44%' },
    { key: 'scene',       label: 'Scene Description', cls: 'notes',     rows: 2, width: '16%' },
  ],
  Skit: [
    { key: 'character', label: 'Character',         cls: 'character', rows: 1, width: '16%' },
    { key: 'line',      label: 'Line',              cls: 'line',      rows: 2, width: '32%' },
    { key: 'notes',     label: 'Directional Notes', cls: 'notes',     rows: 2, width: '16%' },
    { key: 'scene',     label: 'Scene Description', cls: 'notes',     rows: 2, width: '16%' },
    { key: 'props',     label: 'Props',             cls: 'notes',     rows: 1, width: '16%' },
  ],
  TH: [
    { key: 'narration', label: 'Narration', cls: 'line',  rows: 3, width: '36%' },
    { key: 'aRoll',     label: 'A Roll',    cls: 'notes', rows: 2, width: '12%' },
    { key: 'bRoll',     label: 'B Roll',    cls: 'notes', rows: 2, width: '12%' },
    { key: 'vfx',       label: 'VFX',       cls: 'notes', rows: 2, width: '12%' },
    { key: 'sfx',       label: 'SFX',       cls: 'notes', rows: 2, width: '12%' },
    { key: 'props',     label: 'Props',     cls: 'notes', rows: 1, width: '12%' },
  ],
  AI: [
    { key: 'narration', label: 'Narration', cls: 'line',  rows: 3 },
    { key: 'visuals',   label: 'Visuals',   cls: 'notes', rows: 3 },
  ],
  Mockumentary: [
    { key: 'character', label: 'Character',          cls: 'character', rows: 1, width: '13%' },
    { key: 'line',      label: 'Line',               cls: 'line',      rows: 3, width: '22%' },
    { key: 'notes',     label: 'Directional Notes',  cls: 'notes',     rows: 2, width: '13%' },
    { key: 'aroll',     label: 'A-Roll',             cls: 'notes',     rows: 2, width: '10%' },
    { key: 'broll',     label: 'B-Roll',             cls: 'notes',     rows: 2, width: '10%' },
    { key: 'vfx',       label: 'FX',                 cls: 'notes',     rows: 2, width: '11%' },
    { key: 'props',     label: 'Props',              cls: 'notes',     rows: 1, width: '11%' },
    { key: 'location',  label: 'Location',           cls: 'notes',     rows: 1, width: '10%' },
  ],
  // Phase 1 of the Cooking workflow. Practical on-camera reference document,
  // not a creative script. The Recipe column accepts plain text OR a URL
  // (auto-rendered as a clickable link via URL_TEXT_COLS below).
  Recipe: [
    { key: 'recipe',   label: 'Recipe',   cls: 'line',  rows: 2, width: '55%' },
    { key: 'gestures', label: 'Gestures', cls: 'notes', rows: 2, width: '45%' },
  ],
};

// ── Production Notes column ─────────────────────────────────────────────────
// A column the shoot crew fills in once a script is approved (what actually
// happened vs. what was scripted). It is NOT part of FORMAT_COLS on purpose:
// FORMAT_COLS feeds positional importers/paste (parseScenesPositional,
// mapClipRowsToDestination, CSV/TSV/HTML import) where injecting a column would
// shift every mapping. Instead it's spliced into the *display/export* column
// list only, via displayCols(). Inserted immediately after each format's main
// narration/dialogue column. AI is intentionally absent (no physical shoot).
export const PROD_NOTES_COL = { key: 'prodNotes', label: 'Production Notes', cls: 'notes', rows: 2 };
export const PROD_NOTES_INSERT_IDX = { TH: 1, Skit: 2, Mockumentary: 2, Recipe: 1, Longform: 3 };
export function displayCols(format, includeProdNotes) {
  // DEF-104: an unknown / legacy format ('None', null) must never render ZERO
  // columns — that hid a fully-written 60-row script behind a blank grid. Fall
  // back to the Skit layout so the rows stay visible and editable.
  const base = FORMAT_COLS[format] || FORMAT_COLS.Skit;
  const at = PROD_NOTES_INSERT_IDX[format];
  if (!includeProdNotes || at == null) return base;
  const out = base.slice();
  out.splice(at, 0, PROD_NOTES_COL);
  return out;
}

// FIX-1: a row whose production card is in production (the API's `stage`, from
// lib/scriptStage) shows Filming, the shelf it sits on, instead of its qual
// status. An Incomplete card is stage Filming. Without `stage` (older API) the
// row shows qualStatus as before.
export const IN_PRODUCTION_STAGES = ['Filming', 'Filmed', 'Editing', 'Qual', 'Approved', 'Posted'];
export const listStatus = s =>
  (s.qualStatus !== 'Posted' && IN_PRODUCTION_STAGES.includes(s.stage)) ? 'Filming' : s.qualStatus;

// Row and cell highlight colours. `value` is what the script body stores (_rowColor / _cellColors) and what the print
// and Word exports paint, so it stays exactly as before; the editor shows each one as a role tint (`tint`, DESIGN rev 2.1
// §2.1: no blue or violet in the product). Purple and Cyan are no longer offered; rows that carry them still show
// (Purple as the accent tint, Cyan as the info tint). A colour pasted in from a spreadsheet shows as the neutral tint.
export const ROW_COLORS = [
  { value: 'rgba(255,213,66,0.3)',   label: 'Yellow', tint: 'warn' },
  { value: 'rgba(80,200,120,0.3)',   label: 'Green',  tint: 'ok' },
  { value: 'rgba(99,149,255,0.3)',   label: 'Teal',   tint: 'info' },
  { value: 'rgba(255,100,100,0.3)',  label: 'Red',    tint: 'danger' },
  { value: 'rgba(200,100,255,0.3)',  label: 'Purple', tint: 'accent', retired: true },
  { value: 'rgba(255,160,50,0.3)',   label: 'Orange', tint: 'accent' },
  { value: 'rgba(50,220,220,0.3)',   label: 'Cyan',   tint: 'info', retired: true },
  { value: 'rgba(180,180,180,0.25)', label: 'Gray',   tint: 'neutral' },
  { value: null,                     label: 'Clear' },
];
export const tintOf = value => (value ? (ROW_COLORS.find(c => c.value === value)?.tint || 'neutral') : undefined);

// Column labels in sentence case for the editor's header and placeholders ("Scene Description" -> "Scene description");
// all-caps words (VFX, SFX) and the first word keep their case. The exports keep the stored labels.
export const sentenceLabel = label => String(label || '').split(' ')
  .map((w, i) => (i === 0 || (w.length > 1 && w === w.toUpperCase()) ? w : w.toLowerCase())).join(' ');

// ── Characters column: the display + match rules, in one place ────────────────
// Both the tags and the header search's suggestions read through these, so a
// value the column refuses to show can never be offered as something to search
// for (and vice versa).

// Does this value contain at least one letter? The body's character column is a
// free-text cell, and real data holds plenty that is not a name: `...`, `???`,
// `&`, `,`, `3` (DEF-081). Nothing without a letter in it can be a character, so
// the column suppresses it. Anything WITH a letter shows exactly as typed —
// `asdf` and `Camera Pan` included. Deciding which letter-bearing strings are
// "really" names is a writer-convention call, not this column's job.
// \p{L} rather than [A-Za-z]: an accented or non-Latin name is still a name.
export const hasLetter = s => /\p{L}/u.test(s || '');

export const displayCharacters = names => (Array.isArray(names) ? names.filter(hasLetter) : []);

// WORD-PREFIX match, case-insensitive — moved here verbatim from the retired
// toolbar Character dropdown so the header search behaves the way that control
// did. The query must start the NAME or start one of its WORDS: "brit" finds
// `Brittany` and `Chad & Brittany`, "rit" finds neither. Splitting on anything
// non-alphanumeric makes `/`, `&`, `#` and `-` boundaries, which matters because
// character names are routinely multi-word (`Hype Man #1`, `Ashley/Debbie`) and
// someone typing "debbie" plainly means the second one (DEF-074, D-017).
// Whole-string matches rank above word matches.
//
// The server's ?characterPrefix= mirrors this condition for condition
// (characterPrefixCondition in scripts.js). They have to agree: this function
// decides what the search OFFERS, and that one decides what it RETURNS.
export function matchCharacterNames(query, options) {
  const q = (query || '').trim().toLowerCase();
  if (!q) return [];
  const starts = [], word = [];
  for (const n of options) {
    const l = n.toLowerCase();
    if (l.startsWith(q)) { starts.push(n); continue; }
    if (l.split(/[^a-z0-9]+/).some(w => w && w.startsWith(q))) word.push(n);
  }
  return [...starts, ...word];
}

// Is this cell value already in the client's registry? Case-insensitive under
// the shared trim rule (all-whitespace trim, matching the server's charExpr).
export function registryHasName(registryNames, value) {
  const v = String(value || '').replace(/^\s+|\s+$/g, '').toLowerCase();
  if (!v) return true; // an empty cell is not "not in list"
  return registryNames.some(n => String(n).replace(/^\s+|\s+$/g, '').toLowerCase() === v);
}

// The repeat-previous-speaker source: the character TWO rows above — the other
// side of the exchange (Alt+T / Enter-on-empty, Owner decision 1; Ctrl+T is
// browser-reserved and deliberately unused).
export function speakerTwoAbove(draftBodyRef, idx) {
  return String(draftBodyRef.current?.scenes?.[idx - 2]?.character || '').trim();
}

// Columns that support the explicit 🔗 link button
export const LINK_COLS = {
  TH:           ['vfx', 'props'],
  Skit:         ['notes', 'scene', 'props'],
  Longform:     ['scene'],
  Mockumentary: ['notes', 'vfx', 'props', 'location'],
  Recipe:       ['recipe'],
};

// Columns where raw pasted URLs in cell text are auto-rendered as clickable links
export const URL_TEXT_COLS = {
  TH:           ['vfx'],
  Skit:         ['scene', 'props'],
  Longform:     ['scene'],
  Mockumentary: ['notes', 'vfx', 'props', 'location'],
  Recipe:       ['recipe'],
};

export function extractCellUrl(text) {
  const m = (text || '').match(/https?:\/\/[^\s]+|www\.[^\s]+/);
  if (!m) return null;
  const url = m[0].replace(/[.,;:!?)]+$/, '');
  return url.startsWith('www.') ? 'https://' + url : url;
}

// P0: stable per-row identity for per-cell merge. Every scene carries a unique `id`.
export function uid() {
  try { return crypto.randomUUID(); }
  catch { return 'r' + Date.now().toString(36) + Math.random().toString(36).slice(2, 10); }
}

// Mints ids for scenes missing one and re-mints duplicates (e.g. a copied row) so ids
// stay unique within the body. The server re-normalizes on save and is authoritative;
// this keeps the in-memory draft consistent so copies don't collide before the next save.
export function normalizeSceneIds(scriptBody) {
  if (!scriptBody || !Array.isArray(scriptBody.scenes)) return scriptBody;
  const seen = new Set();
  const scenes = scriptBody.scenes.map(s => {
    let id = s && typeof s.id === 'string' && s.id ? s.id : null;
    if (!id || seen.has(id)) id = uid();
    seen.add(id);
    return { ...s, id };
  });
  return { ...scriptBody, scenes };
}

export function emptyRow(format) {
  const cols = FORMAT_COLS[format] || [];
  return { id: uid(), ...Object.fromEntries(cols.map(c => [c.key, ''])) };
}

export function getAvailableTransitions(script, user) {
  if (!script || !user) return [];
  const isAdmin = user.roles?.includes('Admin') || user.roles?.includes('Power User');
  return Object.entries(TRANSITIONS)
    .filter(([status, rule]) => {
      if (!rule.from.includes(script.qualStatus)) return false;
      if (!rule.roles.some(r => user.roles.includes(r))) return false;
      if (rule.noSelf && !isAdmin && script.writer?.id === user.id) return false;
      // The pull-back out of Approved only exists while the script hasn't been
      // shot — the server refuses it with a 409 once a card has moved past
      // Scripted (productionStarted), so don't offer a button that can't work.
      if (status === 'Needs Review' && script.qualStatus === 'Approved' && script.productionStarted) return false;
      return true;
    })
    .map(([status]) => status);
}

export function transitionBtnLabel(status, script) {
  // Same transition, two directions: forward from I/P is a submission, backward
  // from Approved is a pull-back, and the label has to say which.
  if (status === 'Needs Review') {
    return script?.qualStatus === 'Approved' ? 'Pull back to needs review' : 'Submit for review';
  }
  if (status === 'Approved')       return 'Approve';
  if (status === 'Needs Revision') return 'Needs revision';
  if (status === 'I/P')            return 'Back to in progress';
  return status;
}

// Spec 5.2: extend a column's source values to fill `count` more rows.
// Detects: single-value repeat, pure numeric arithmetic progression,
// text+trailing-number progression ("Scene 1" → "Scene 2"), or falls back
// to cycling through the source values (which also handles A,B,A,B style).
export function detectAndExtendColumn(sourceValues, count) {
  if (count <= 0) return [];
  if (sourceValues.length === 0) return Array(count).fill('');
  if (sourceValues.length === 1) return Array(count).fill(sourceValues[0]);

  // Pure numeric arithmetic progression.
  const nums = sourceValues.map(v => Number(v));
  if (nums.every(n => Number.isFinite(n)) && sourceValues.every(v => String(v).trim() !== '')) {
    const step = nums[1] - nums[0];
    let linear = true;
    for (let i = 2; i < nums.length; i++) {
      if (nums[i] - nums[i - 1] !== step) { linear = false; break; }
    }
    if (linear) {
      const out = [];
      let last = nums[nums.length - 1];
      for (let i = 0; i < count; i++) { last += step; out.push(String(last)); }
      return out;
    }
  }

  // Text-with-trailing-number ("Scene 1" → "Scene 2"). Common prefix required.
  const re = /^(.*?)(-?\d+)$/;
  const parts = sourceValues.map(v => (typeof v === 'string' ? v.match(re) : null));
  if (parts.every(p => p !== null)) {
    const prefixes = parts.map(p => p[1]);
    const numbers  = parts.map(p => Number(p[2]));
    if (prefixes.every(p => p === prefixes[0])) {
      const step = numbers[1] - numbers[0];
      let linear = true;
      for (let i = 2; i < numbers.length; i++) {
        if (numbers[i] - numbers[i - 1] !== step) { linear = false; break; }
      }
      if (linear) {
        const out = [];
        let last = numbers[numbers.length - 1];
        for (let i = 0; i < count; i++) { last += step; out.push(prefixes[0] + last); }
        return out;
      }
    }
  }

  // Fallback: cycle through source values (handles A,B,A,B or any non-linear pattern).
  const out = [];
  for (let i = 0; i < count; i++) out.push(sourceValues[i % sourceValues.length]);
  return out;
}

// Spec 3.1: row-height sync. Measures the natural content height of every
// renderable cell in the row (textareas via scrollHeight after reset, idle
// divs via offsetHeight after min-height reset) and applies the max to all,
// so a wrap in one cell expands all siblings to the same visual height.
// Works through the Phase B1 div↔textarea swap by treating both cell types.
export function syncRowHeights(tr) {
  if (!tr) return;
  const cells = tr.querySelectorAll('.scene-input, .scene-cell-idle');
  if (cells.length === 0) return;
  // Reset first so cells that shrunk give back their natural height.
  for (let i = 0; i < cells.length; i++) {
    const el = cells[i];
    if (el.tagName === 'TEXTAREA') el.style.height = '0px';
    else                           el.style.minHeight = '0px';
  }
  let max = 0;
  for (let i = 0; i < cells.length; i++) {
    const el = cells[i];
    const h = el.tagName === 'TEXTAREA' ? el.scrollHeight : el.offsetHeight;
    if (h > max) max = h;
  }
  const px = max + 'px';
  for (let i = 0; i < cells.length; i++) {
    const el = cells[i];
    if (el.tagName === 'TEXTAREA') el.style.height = px;
    else                           el.style.minHeight = px;
  }
}

export function autoResize(el) {
  // Row-aware: a wrap in this cell expands all cells in the row.
  const tr = el.closest('tr');
  if (tr) { syncRowHeights(tr); return; }
  el.style.height = '0px';
  el.style.height = el.scrollHeight + 'px';
}

// Bulk autoResize that chunks across animation frames so long scripts (200+ rows × 5 cols
// = 1000+ textareas) don't block the main thread for hundreds of ms. Used by initial load,
// column-width drag, font-size change, and window resize. Per-keystroke updates go through
// the smart diffing useLayoutEffect, not this helper. Iterates by row so each row's
// cells share a single max-height pass.
export function autoResizeAll(chunkSize = 40) {
  const rows = document.querySelectorAll('.scene-table tr');
  if (rows.length === 0) return;
  if (rows.length <= chunkSize) {
    rows.forEach(syncRowHeights);
    return;
  }
  let i = 0;
  const step = () => {
    const end = Math.min(i + chunkSize, rows.length);
    for (; i < end; i++) syncRowHeights(rows[i]);
    if (i < rows.length) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

// Render **bold**, *italic*, __underline__ markers as HTML (for print/Word export)
// Call AFTER HTML-escaping so markers aren't affected; process bold before italic to avoid partial matches
export function renderMarkdown(s) {
  return s
    .replace(/\*\*(.+?)\*\*/gs, '<strong>$1</strong>')
    .replace(/__(.+?)__/gs, '<u>$1</u>')
    .replace(/\*([^*]+?)\*/gs, '<em>$1</em>');
}

// A right-click inside an editable text field belongs to the browser (its menu
// carries the spellcheck suggestions, which no web API can read) — a right-click
// on anything else in the table belongs to the app. Every contextmenu handler in
// this file runs this test first and bails without preventDefault() on a match.
// Walks up from the target in case the click lands on a child node.
export function isEditableTarget(el) {
  if (!(el instanceof Element)) return false;
  const t = el.closest('textarea, input, [contenteditable]');
  if (!t) return false;
  if (t.tagName === 'TEXTAREA') return true;
  if (t.tagName === 'INPUT') {
    const type = (t.getAttribute('type') || 'text').toLowerCase();
    return ['text', 'search', 'url', 'tel', 'email', 'password', 'number'].includes(type);
  }
  return t.isContentEditable === true;
}

// ── Drag-select edge auto-scroll constants ────────────────────────────────────
// Hot zone depth measured from the scroll container's edges, and the per-frame
// scroll cap. Velocity is proportional to how deep the pointer sits in the zone
// (a crawl at the zone's inner edge, MAX at/past the container edge).
export const AUTOSCROLL_EDGE_PX = 60;
export const AUTOSCROLL_MAX_STEP_PX = 20;

// Clamp a viewport point to just inside the scroller's visible row area so
// elementFromPoint resolves a row even when the pointer sits outside the
// container (that is where the edge zones are). topEdge already accounts for
// the sticky column header overlaying the top of the scroll area.
export function clampToScrollerRect(x, y, rect, topEdge) {
  return {
    x: Math.min(Math.max(x, rect.left + 2), rect.right - 2),
    y: Math.min(Math.max(y, topEdge + 2), rect.bottom - 2),
  };
}

// Strict row/column splitter. Every \n starts a new row; every delim ends a cell.
// Quotes are NOT honored on import — a stray unbalanced `"` in upstream data used to
// pull every subsequent row into one giant cell. Trade-off: spreadsheet cells with
// embedded newlines (Alt+Enter in Sheets) will split into multiple rows on paste.
export function splitDelimitedRows(text, delim) {
  return text.split('\n').map(line => line.split(delim));
}

// RFC-4180 CSV parser — used ONLY by the file-based import (csvToScenes), whose
// input is our own exportSceneCSV output: every cell wrapped in "...", inner
// quotes doubled ("" -> "), and cells that legitimately contain commas or
// newlines (talking-head narration routinely does both). It honors quoting:
// commas/newlines inside a quoted field are preserved, "" becomes a literal ",
// and the wrapping quotes are stripped. An unterminated final quote is tolerated
// (EOF closes the field) so a single stray " can't swallow the rest of the file
// — the exact failure the naive splitDelimitedRows was guarding against, which
// is why paste (parseScenesPositional) is left on the naive splitter untouched.
export function parseCSVRows(text) {
  const rows = [];
  let row = [], field = '', inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { field += '"'; i++; }  // escaped quote
        else inQuotes = false;                            // close quote
      } else field += ch;
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      row.push(field); field = '';
    } else if (ch === '\n') {
      row.push(field); rows.push(row); row = []; field = '';
    } else field += ch;
  }
  row.push(field); rows.push(row);  // flush trailing field/row (no final newline)
  return rows;
}

// Positional paste \u2014 clipboard cells map column-by-column to the format's data columns,
// starting at the top-left. No header detection. Extra clipboard columns past the format's
// column count are dropped silently; missing columns stay empty. Each clipboard row becomes
// one script row, in order \u2014 including rows where every cell happens to be blank, since
// they may have data in clipboard columns the format doesn't slot. The only thing we strip
// is purely-blank rows at the trailing edge (corner-select artifacts from spreadsheets).
export function parseScenesPositional(text, format, delim) {
  const cols = FORMAT_COLS[format] || [];
  if (!cols.length) return [];
  const normalized = text.replace(/^\ufeff/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const rawRows = splitDelimitedRows(normalized, delim);
  // Trim trailing rows that are entirely empty across ALL clipboard columns.
  let end = rawRows.length;
  while (end > 0 && rawRows[end - 1].every(c => !c || !c.trim())) end--;
  return rawRows.slice(0, end).map(vals => {
    const row = emptyRow(format);
    cols.forEach((c, i) => {
      if (vals[i] !== undefined) {
        // Don't trim internal newlines \u2014 only outer spaces/tabs.
        row[c.key] = String(vals[i]).replace(/^[ \t]+|[ \t]+$/g, '');
      }
    });
    return row;
  });
}

// Header-matched parse used by the file-based CSV import (which has a Download template flow).
// Quote-aware (parseCSVRows) so it round-trips our own quoted CSV export cleanly \u2014
// commas/newlines inside cells stay put and wrapping quotes are stripped.
export function csvToScenes(text, format) {
  const cols = FORMAT_COLS[format] || [];
  if (!cols.length) return [];
  const labelToKey = {};
  cols.forEach(c => {
    labelToKey[c.label.toLowerCase()] = c.key;
    labelToKey[c.key.toLowerCase()]   = c.key;
  });
  const normalized = text.replace(/^\ufeff/, '').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  const rows = parseCSVRows(normalized);
  if (rows.length < 2) return [];
  const headerKeys = rows[0].map(h => labelToKey[h.trim().replace(/^"|"$/g, '').toLowerCase()] || null);
  return rows.slice(1).map(vals => {
    const row = emptyRow(format);
    headerKeys.forEach((key, i) => {
      if (key && vals[i] !== undefined) {
        row[key] = String(vals[i]).replace(/^[ \t]+|[ \t]+$/g, '');
      }
    });
    return row;
  }).filter(row => cols.some(c => (row[c.key] || '').trim().length > 0));
}

// \u2500\u2500 Clipboard TSV export \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
// Strip **bold**, *italic*, __underline__ markers \u2014 output plain text only.
export function stripScriptMarkdown(s) {
  return (s || '')
    .replace(/\*\*(.+?)\*\*/gs, '$1')
    .replace(/__(.+?)__/gs, '$1')
    .replace(/\*([^*]+?)\*/gs, '$1');
}

// TSV cell escape \u2014 wrap in double quotes if cell contains tab, newline, or quote.
export function tsvEscapeCell(val) {
  const s = stripScriptMarkdown(val || '');
  if (/[\t\n\r"]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
  return s;
}

// Build TSV string for the current script (data rows only \u2014 no header row, per spec).
export function buildScriptTSV(scenes, format) {
  const cols = FORMAT_COLS[format] || [];
  if (!cols.length || !Array.isArray(scenes)) return '';
  return scenes
    .map(row => cols.map(c => tsvEscapeCell(row[c.key])).join('\t'))
    .join('\n');
}

// Build a TSV string for an arbitrary subset of rows/columns (used by the app
// clipboard so a partial selection round-trips to Google Sheets / Excel too).
export function buildSubsetTSV(rows, colKeys) {
  if (!Array.isArray(rows) || !Array.isArray(colKeys) || !colKeys.length) return '';
  return rows
    .map(row => colKeys.map(k => tsvEscapeCell(row[k])).join('\t'))
    .join('\n');
}

// ── App-clipboard column mapping ────────────────────────────────────────────
// Maps copied rows from a source format/selection onto a destination format.
// All three cases collapse to one index-aligned operation:
//   • same-format full rows  → identity (1:1 by column, full visual fidelity)
//   • cross-format full rows → positional (source col index N → dest col index N)
//   • partial-column paste   → positional, restricted to the selected columns
// Positional cross-format matches the existing Google-Sheets paste behavior
// (parseScenesPositional) rather than name-matching, since real formats barely
// overlap by column name. Returns { scenes, crossFormat }.
export function mapClipRowsToDestination(clipPayload, destFormat, { columnMode } = {}) {
  const destCols   = FORMAT_COLS[destFormat] || [];
  const srcCols    = FORMAT_COLS[clipPayload.sourceFormat] || [];
  const sameFormat = clipPayload.sourceFormat === destFormat;
  const mode = columnMode || (clipPayload.selectionType === 'partial_cells' ? 'selected' : 'full');
  const scenes = (clipPayload.rows || []).map(clipRow => {
    const dest = emptyRow(destFormat);
    if (mode === 'selected') {
      // Each selected source column lands in the destination column at the same
      // index. Same-format → same index → effectively 1:1 by name.
      for (const srcKey of (clipPayload.selectedColumns || [])) {
        const srcIdx = srcCols.findIndex(c => c.key === srcKey);
        if (srcIdx < 0) continue;
        const destCol = destCols[srcIdx];
        if (destCol && clipRow[srcKey] != null) dest[destCol.key] = clipRow[srcKey];
      }
    } else if (sameFormat) {
      for (const c of destCols) if (clipRow[c.key] != null) dest[c.key] = clipRow[c.key];
      if (clipRow._rowColor)   dest._rowColor   = clipRow._rowColor;
      if (clipRow._cellColors) dest._cellColors = { ...clipRow._cellColors };
      if (clipRow._links)      dest._links      = JSON.parse(JSON.stringify(clipRow._links));
    } else {
      // Cross-format full rows: positional, truncated to the shorter format.
      // Cell colors/links are keyed by source column keys absent in the dest, so
      // drop them; keep only the row color.
      srcCols.forEach((sc, i) => {
        const dc = destCols[i];
        if (dc && clipRow[sc.key] != null) dest[dc.key] = clipRow[sc.key];
      });
      if (clipRow._rowColor) dest._rowColor = clipRow._rowColor;
    }
    return dest;
  });
  return { scenes, crossFormat: !sameFormat };
}

// \u2500\u2500 Clipboard HTML round-trip \u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500\u2500
// Writing HTML on copy (alongside TSV) lets cell colors, row colors, and hyperlinks
// survive Heliconflow\u2192Heliconflow paste AND show up in Google Sheets paste. data-hf-*
// attributes give clean Heliconflow round-trip; inline style="background-color:..." and
// <a href="..."> tags handle Sheets compatibility.

export function escapeHtml(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

export function escapeAttr(s) {
  return String(s == null ? '' : s)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;');
}

export function buildScriptHTML(scenes, format) {
  const cols = FORMAT_COLS[format] || [];
  if (!cols.length || !Array.isArray(scenes)) return '';
  const trs = scenes.map(row => {
    const rowColor = row._rowColor;
    const rowAttrs = rowColor
      ? ` style="background-color:${escapeAttr(rowColor)}" data-hf-row-color="${escapeAttr(rowColor)}"`
      : '';
    const tds = cols.map(c => {
      const text = stripScriptMarkdown(row[c.key] || '');
      const cellColor = row._cellColors?.[c.key];
      const link = row._links?.[c.key];
      const styleAttr = cellColor ? ` style="background-color:${escapeAttr(cellColor)}"` : '';
      const attrs = [];
      if (cellColor) attrs.push(`data-hf-cell-color="${escapeAttr(cellColor)}"`);
      if (link?.url) {
        attrs.push(`data-hf-link-url="${escapeAttr(link.url)}"`);
        if (link.text) attrs.push(`data-hf-link-text="${escapeAttr(link.text)}"`);
      }
      const attrStr = attrs.length ? ' ' + attrs.join(' ') : '';
      const textHtml = escapeHtml(text).replace(/\n/g, '<br>');
      const linkHtml = link?.url
        ? `<a href="${escapeAttr(link.url)}">${escapeHtml(link.text || link.url)}</a>`
        : '';
      const inner = text && linkHtml ? `${textHtml} ${linkHtml}` : (textHtml || linkHtml);
      return `<td${styleAttr}${attrStr}>${inner}</td>`;
    }).join('');
    return `<tr${rowAttrs}>${tds}</tr>`;
  }).join('');
  return `<table>${trs}</table>`;
}

// Walk a <td>'s child nodes and assemble text content. <br>/<p>/<div> become \n.
// <a> children are skipped \u2014 the caller extracts the link separately so its text
// doesn't double up inside the cell value.
export function htmlCellToText(el) {
  let out = '';
  for (const node of el.childNodes) {
    if (node.nodeType === 3) {
      out += node.nodeValue;
    } else if (node.nodeType === 1) {
      const tag = node.tagName;
      if (tag === 'BR') {
        out += '\n';
      } else if (tag === 'P' || tag === 'DIV') {
        if (out && !out.endsWith('\n')) out += '\n';
        out += htmlCellToText(node);
      } else if (tag === 'A') {
        // skipped \u2014 extracted separately as a link
      } else {
        out += htmlCellToText(node);
      }
    }
  }
  return out;
}

export function extractBgColor(el) {
  const style = el.getAttribute && el.getAttribute('style');
  if (!style) return null;
  const m = style.match(/background(?:-color)?\s*:\s*([^;]+)/i);
  if (!m) return null;
  const val = m[1].trim();
  if (!val || /^(transparent|inherit|initial|unset|none)$/i.test(val)) return null;
  return val;
}

export function isRowEffectivelyEmpty(row, cols) {
  if (cols.some(c => (row[c.key] || '').trim())) return false;
  if (row._rowColor) return false;
  if (row._cellColors && Object.keys(row._cellColors).length) return false;
  if (row._links && Object.keys(row._links).length) return false;
  return true;
}

export function parseScenesFromHTML(html, format) {
  const cols = FORMAT_COLS[format] || [];
  if (!cols.length || !html) return [];
  let doc;
  try {
    doc = new DOMParser().parseFromString(html, 'text/html');
  } catch {
    return [];
  }
  const trs = doc.querySelectorAll('tr');
  if (trs.length === 0) return [];
  const scenes = [];
  trs.forEach(tr => {
    const row = emptyRow(format);
    const rowColor = tr.getAttribute('data-hf-row-color') || extractBgColor(tr);
    if (rowColor) row._rowColor = rowColor;
    const tds = tr.querySelectorAll('td, th');
    cols.forEach((c, i) => {
      const td = tds[i];
      if (!td) return;
      // Cell color
      const cellColor = td.getAttribute('data-hf-cell-color') || extractBgColor(td);
      if (cellColor) {
        if (!row._cellColors) row._cellColors = {};
        row._cellColors[c.key] = cellColor;
      }
      // Link \u2014 prefer our own attrs, fall back to the first <a> in the cell
      const linkUrlAttr = td.getAttribute('data-hf-link-url');
      const linkTextAttr = td.getAttribute('data-hf-link-text');
      if (linkUrlAttr) {
        if (!row._links) row._links = {};
        row._links[c.key] = { url: linkUrlAttr, text: linkTextAttr || '' };
      } else {
        const a = td.querySelector('a[href]');
        if (a) {
          if (!row._links) row._links = {};
          row._links[c.key] = { url: a.getAttribute('href'), text: a.textContent };
          a.remove(); // strip so its text isn't double-counted in the cell value
        }
      }
      // Cell text \u2014 htmlCellToText skips <a> children, which is what we want
      const text = htmlCellToText(td).replace(/^[ \t]+|[ \t]+$/g, '');
      row[c.key] = text;
    });
    scenes.push(row);
  });
  // Trim trailing rows that are entirely empty across text AND metadata.
  while (scenes.length && isRowEffectivelyEmpty(scenes[scenes.length - 1], cols)) {
    scenes.pop();
  }
  return scenes;
}

export function downloadTemplate(format) {
  const cols = FORMAT_COLS[format];
  if (!cols) return;
  const header = cols.map(c => `"${c.label}"`).join(',');
  const blob = new Blob([header + '\n'], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `${format.toLowerCase()}-script-template.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
